import { Check, ChevronsUpDown, X } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Badge } from './badge';
import { Button } from './button';
import { Input } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';

export type ComboboxType = 'single' | 'multiple';

export interface ComboboxProps<T> {
  data: T[];
  keyExtractor: (item: T) => string;
  displayValueGetter: (item: T) => string;
  value?: T[];
  defaultValue?: T[];
  placeholder?: string;
  type?: ComboboxType;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  id?: string;
  /** Disable local filtering (useful for server-side search) */
  disableLocalFilter?: boolean;
  onOptionChange?: (selectedOptions: T[]) => void;
  onSearchChange?: (queryText: string, selectedOptions: T[]) => void;
}

export function Combobox<T>({
  data,
  keyExtractor,
  displayValueGetter,
  value,
  defaultValue = [],
  placeholder = 'Selecione uma opção',
  type = 'single',
  disabled = false,
  isLoading = false,
  className,
  id,
  disableLocalFilter = false,
  onOptionChange,
  onSearchChange,
}: ComboboxProps<T>) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [selectedOptions, setSelectedOptions] = React.useState<T[]>(defaultValue);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const currentSelection = value ?? selectedOptions;

  // Filter data based on query (skip filtering if disableLocalFilter is true)
  const filteredData = React.useMemo(() => {
    if (disableLocalFilter) return data;
    if (!query) return data;
    return data.filter((item) =>
      displayValueGetter(item).toLowerCase().includes(query.toLowerCase()),
    );
  }, [data, query, displayValueGetter, disableLocalFilter]);

  // Handle selection
  const handleSelect = React.useCallback(
    (item: T) => {
      if (type === 'single') {
        const newSelection = [item];
        if (!value) {
          setSelectedOptions(newSelection);
        }
        onOptionChange?.(newSelection);
        setOpen(false);
        setQuery('');
      } else {
        const itemKey = keyExtractor(item);
        const isSelected = currentSelection.some((selected) => keyExtractor(selected) === itemKey);

        let newSelection: T[];
        if (isSelected) {
          newSelection = currentSelection.filter((selected) => keyExtractor(selected) !== itemKey);
        } else {
          newSelection = [...currentSelection, item];
        }

        if (!value) {
          setSelectedOptions(newSelection);
        }
        onOptionChange?.(newSelection);
      }
    },
    [type, value, currentSelection, keyExtractor, onOptionChange],
  );

  // Handle search input
  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newQuery = e.target.value;
      setQuery(newQuery);
      onSearchChange?.(newQuery, currentSelection);
    },
    [currentSelection, onSearchChange],
  );

  // Handle clear
  const handleClear = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setQuery('');
      if (!value) {
        setSelectedOptions([]);
      }
      onOptionChange?.([]);
    },
    [value, onOptionChange],
  );

  // Handle removing a single item (for multi-select badges)
  const handleRemoveItem = React.useCallback(
    (item: T, e: React.MouseEvent) => {
      e.stopPropagation();
      const itemKey = keyExtractor(item);
      const newSelection = currentSelection.filter(
        (selected) => keyExtractor(selected) !== itemKey,
      );
      if (!value) {
        setSelectedOptions(newSelection);
      }
      onOptionChange?.(newSelection);
    },
    [value, currentSelection, keyExtractor, onOptionChange],
  );

  // Get display value for trigger
  const displayValue = React.useMemo(() => {
    if (type === 'single' && currentSelection.length > 0) {
      return displayValueGetter(currentSelection[0]);
    }
    if (type === 'multiple' && currentSelection.length > 0) {
      return `${currentSelection.length} selecionado(s)`;
    }
    return '';
  }, [type, currentSelection, displayValueGetter]);

  // Focus input when popover opens
  React.useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const isMultiWithSelection = type === 'multiple' && currentSelection.length > 0;

  const MAX_VISIBLE_BADGES = 3;
  const visibleItems = currentSelection.slice(0, MAX_VISIBLE_BADGES);
  const hiddenCount = currentSelection.length - MAX_VISIBLE_BADGES;

  const [allOpen, setAllOpen] = React.useState(false);

  const triggerButton = isMultiWithSelection ? (
    <div
      id={id}
      role="combobox"
      tabIndex={0}
      aria-expanded={open}
      className={cn(
        'flex min-h-9 w-full items-start rounded-md border border-input bg-transparent px-3 py-1.5 text-sm shadow-xs',
        'transition-colors hover:bg-accent/50',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      <div className="flex flex-1 flex-wrap gap-1 min-w-0">
        {visibleItems.map((item) => (
          <Badge
            key={keyExtractor(item)}
            variant="secondary"
            className="shrink-0 max-w-full gap-1 pr-1"
          >
            <span className="truncate">{displayValueGetter(item)}</span>
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: nested inside combobox trigger */}
            {/* biome-ignore lint/a11y/noStaticElementInteractions: nested inside combobox trigger */}
            <span
              tabIndex={-1}
              className="ml-0.5 rounded-sm p-0.5 hover:bg-gray-300/50 cursor-pointer"
              onClick={(e) => handleRemoveItem(item, e)}
              onPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <X className="h-3 w-3" />
            </span>
          </Badge>
        ))}
        {hiddenCount > 0 && (
          <Popover open={allOpen} onOpenChange={setAllOpen}>
            <PopoverTrigger asChild>
              {/* biome-ignore lint/a11y/useKeyWithClickEvents: nested inside combobox trigger */}
              {/* biome-ignore lint/a11y/noStaticElementInteractions: nested inside combobox trigger */}
              <span
                className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground cursor-pointer px-1 py-0.5"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                Ver todos ({currentSelection.length})
              </span>
            </PopoverTrigger>
            <PopoverContent
              className="w-64 max-h-60 overflow-auto p-2"
              align="start"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <div className="flex flex-wrap gap-1">
                {currentSelection.map((item) => (
                  <Badge
                    key={keyExtractor(item)}
                    variant="secondary"
                    className="shrink-0 max-w-full gap-1 pr-1"
                  >
                    <span className="truncate">{displayValueGetter(item)}</span>
                    {/* biome-ignore lint/a11y/useKeyWithClickEvents: inside popover */}
                    {/* biome-ignore lint/a11y/noStaticElementInteractions: inside popover */}
                    <span
                      tabIndex={-1}
                      className="ml-0.5 rounded-sm p-0.5 hover:bg-gray-300/50 cursor-pointer"
                      onClick={(e) => {
                        handleRemoveItem(item, e);
                      }}
                    >
                      <X className="h-3 w-3" />
                    </span>
                  </Badge>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
      <div className="flex items-center gap-1 ml-2 shrink-0 pt-0.5">
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: nested inside combobox trigger */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: nested inside combobox trigger */}
        <span
          tabIndex={-1}
          className="relative z-10 p-0.5 rounded-sm hover:bg-accent cursor-pointer"
          onClick={handleClear}
          onPointerDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <X className="h-3.5 w-3.5 opacity-50 hover:opacity-100" />
        </span>
        <ChevronsUpDown className="h-4 w-4 opacity-50" />
      </div>
    </div>
  ) : (
    <Button
      id={id}
      variant="outline"
      role="combobox"
      aria-expanded={open}
      className={cn(
        'w-full justify-between font-normal overflow-hidden',
        !displayValue && 'text-muted-foreground',
        className,
      )}
      disabled={disabled}
    >
      <span className="truncate flex-1 min-w-0">{displayValue || placeholder}</span>
      <div className="flex items-center gap-1 ml-2 shrink-0">
        {currentSelection.length > 0 && (
          // biome-ignore lint/a11y/useKeyWithClickEvents: nested inside button, can't use <button>
          // biome-ignore lint/a11y/noStaticElementInteractions: nested inside button, can't use <button>
          <span
            tabIndex={-1}
            className="relative z-10 p-0.5 rounded-sm hover:bg-accent cursor-pointer"
            onClick={handleClear}
            onPointerDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <X className="h-3.5 w-3.5 opacity-50 hover:opacity-100" />
          </span>
        )}
        <ChevronsUpDown className="h-4 w-4 opacity-50" />
      </div>
    </Button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        {triggerButton}
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        {/* Search Input */}
        <div className="p-2 border-b">
          <Input
            ref={inputRef}
            placeholder="Buscar..."
            value={query}
            onChange={handleSearchChange}
            className="h-8"
          />
        </div>

        {/* Options List */}
        <div className="max-h-60 overflow-auto">
          {isLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Carregando...</div>
          ) : filteredData.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Nenhum resultado encontrado.
            </div>
          ) : (
            <div className="p-1">
              {filteredData.map((item) => {
                const itemKey = keyExtractor(item);
                const isSelected = currentSelection.some(
                  (selected) => keyExtractor(selected) === itemKey,
                );

                return (
                  <TooltipProvider key={itemKey} delayDuration={400}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => handleSelect(item)}
                          className={cn(
                            'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
                            'hover:bg-accent hover:text-accent-foreground',
                            'focus:bg-accent focus:text-accent-foreground',
                            isSelected && 'bg-accent',
                          )}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4 shrink-0',
                              isSelected ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          <span className="truncate">{displayValueGetter(item)}</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs break-words">
                        {displayValueGetter(item)}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
